import { action, computed, makeObservable, observable } from 'mobx'

import { DatesFormats } from '@new/__global/components/FastTapeChart/__types'

import {
  DAY_COLUMN_WIDTH,
  DEFAULT_DATES_FORMATS,
  EXTENDED_COLUMN_WIDTH,
  FIRST_COLUMN_LEFT_PADDING,
  FIRST_COLUMN_WIDTH,
  TAPE_CHART_ROW_BASE_HEIGHT,
} from '../config/contants'

const LOCAL_STORAGE_MAXIMISED_KEY = 'tapeChartMaximised'

class TapeChartGridSizingStore {
  isMobile = false

  private _isFirstColumnCollapsed = false
  private _isGridMaximised = Boolean(localStorage.getItem(LOCAL_STORAGE_MAXIMISED_KEY))

  private _customFirstColumnWidth?: number
  private _customFirstColumnLeftPadding?: number
  private _customDayColumnWidth?: number
  private _customPropertyRowHeight?: number

  private _datesFormats?: DatesFormats

  constructor() {
    makeObservable<
      TapeChartGridSizingStore,
      | '_isFirstColumnCollapsed'
      | '_isGridMaximised'
      | '_customFirstColumnWidth'
      | '_customFirstColumnLeftPadding'
      | '_customDayColumnWidth'
      | '_customPropertyRowHeight'
      | '_datesFormats'
    >(this, {
      isMobile: observable,
      _isFirstColumnCollapsed: observable,
      _isGridMaximised: observable,
      _customFirstColumnWidth: observable,
      _customFirstColumnLeftPadding: observable,
      _customDayColumnWidth: observable,
      _customPropertyRowHeight: observable,
      _datesFormats: observable,
      stickyButtons: computed,
      isFirstColumnCollapsed: computed,
      isGridMaximised: computed,
      firstColumnWidth: computed,
      firstColumnLeftPadding: computed,
      extendedColumnWidth: computed,
      dayColumnWidth: computed,
      propertyRowHeight: computed,
      datesFormats: computed,
      toggleFirstColumn: action.bound,
      setGridSizing: action.bound,
      setCustomFirstColumnWidth: action.bound,
      setCustomFirstColumnLeftPadding: action.bound,
      setCustomDayColumnWidth: action.bound,
      setCustomPropertyRowHeight: action.bound,
      setCustomDateFormat: action.bound,
    })
  }

  get stickyButtons() {
    return !this.isMobile
  }

  get isFirstColumnCollapsed() {
    return this._isFirstColumnCollapsed
  }

  get isGridMaximised() {
    return this._isGridMaximised
  }

  get firstColumnWidth() {
    if (this._customFirstColumnWidth !== undefined) return this._customFirstColumnWidth
    if (!this.isMobile) return FIRST_COLUMN_WIDTH.DESKTOP
    return this._isFirstColumnCollapsed
      ? FIRST_COLUMN_WIDTH.MOBILE_COLLAPSED
      : FIRST_COLUMN_WIDTH.MOBILE
  }

  get firstColumnLeftPadding() {
    if (this._customFirstColumnLeftPadding !== undefined) return this._customFirstColumnLeftPadding
    if (!this.isMobile) return FIRST_COLUMN_LEFT_PADDING.DESKTOP
    return this._isFirstColumnCollapsed
      ? FIRST_COLUMN_LEFT_PADDING.MOBILE_COLLAPSED
      : FIRST_COLUMN_LEFT_PADDING.MOBILE
  }

  get extendedColumnWidth() {
    if (!this.isMobile) return EXTENDED_COLUMN_WIDTH.DESKTOP
    return this._isFirstColumnCollapsed
      ? EXTENDED_COLUMN_WIDTH.MOBILE_COLLAPSED
      : EXTENDED_COLUMN_WIDTH.MOBILE
  }

  get dayColumnWidth() {
    if (this._customDayColumnWidth !== undefined) return this._customDayColumnWidth
    if (this.isMobile) return DAY_COLUMN_WIDTH.MOBILE
    return this.isGridMaximised ? DAY_COLUMN_WIDTH.DESKTOP_MAXIMISED : DAY_COLUMN_WIDTH.DESKTOP
  }

  get propertyRowHeight() {
    if (this._customPropertyRowHeight !== undefined) return this._customPropertyRowHeight
    if (this.isMobile) return TAPE_CHART_ROW_BASE_HEIGHT.MOBILE
    return this.isGridMaximised
      ? TAPE_CHART_ROW_BASE_HEIGHT.DESKTOP_MAXIMISED
      : TAPE_CHART_ROW_BASE_HEIGHT.DESKTOP
  }

  get datesFormats(): DatesFormats {
    return { ...DEFAULT_DATES_FORMATS, ...this._datesFormats }
  }

  toggleFirstColumn() {
    this._isFirstColumnCollapsed = !this._isFirstColumnCollapsed
  }

  setGridSizing(maximised: boolean) {
    this._isGridMaximised = maximised
    if (maximised) {
      localStorage.setItem(LOCAL_STORAGE_MAXIMISED_KEY, 'true')
    } else {
      localStorage.removeItem(LOCAL_STORAGE_MAXIMISED_KEY)
    }
  }

  setCustomFirstColumnWidth(width: number) {
    this._customFirstColumnWidth = width
  }

  setCustomFirstColumnLeftPadding(padding: number) {
    this._customFirstColumnLeftPadding = padding
  }

  setCustomDayColumnWidth(width: number) {
    this._customDayColumnWidth = width
  }

  setCustomPropertyRowHeight(height: number) {
    this._customPropertyRowHeight = height
  }

  setCustomDateFormat(dateFormats?: DatesFormats) {
    this._datesFormats = { ...this._datesFormats, ...dateFormats }
  }
}

export const tapeChartGridSizingStore = new TapeChartGridSizingStore()
